import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError } from '../common/ApiError';
import { setDaysOff } from '../settings/api';
import { useSettings } from '../settings/SettingsProvider';
import type { Caller } from './models';
import { useCurrentUserState } from './useCurrentUserState.hook';

type CallerStatus = 'loading' | 'ready' | 'error';

export type CurrentUserContextValue = {
    status: CallerStatus;
    user: Caller | null;
    daysOff: Set<string>;
    daysOffVersion: number;
    canEditDaysOff: boolean;
    toggleDayOff: (date: string) => Promise<void>;
    error: string | null;
};

export const CurrentUserContext = createContext<CurrentUserContextValue | undefined>(undefined);

export const CurrentUserProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { configured, settings } = useSettings();
    const loaded = useCurrentUserState(configured, settings.org, settings.pat);

    const [daysOff, setDaysOffState] = useState<Set<string>>(new Set());
    const [etag, setEtag] = useState<string | null>(null);
    const [daysOffVersion, setDaysOffVersion] = useState(0);
    const [localError, setLocalError] = useState<string | null>(null);

    useEffect(() => {
        setDaysOffState(loaded.daysOff);
        setEtag(loaded.etag);
        setLocalError(null);
    }, [loaded.daysOff, loaded.etag]);

    const toggleDayOff = useCallback(
        async (date: string) => {
            if (!loaded.user || etag === null) {
                return;
            }
            const next = new Set(daysOff);
            if (next.has(date)) {
                next.delete(date);
            } else {
                next.add(date);
            }
            try {
                const result = await setDaysOff(loaded.user.email, { daysOff: [...next].toSorted() }, etag);
                setDaysOffState(new Set(result.member.daysOff));
                setEtag(result.etag);
                setDaysOffVersion((v) => v + 1);
                setLocalError(null);
            } catch (e) {
                if (e instanceof ApiError && e.status === 409) {
                    setLocalError('Days-off changed in another tab — reload to see the latest.');
                } else {
                    setLocalError(e instanceof Error ? e.message : 'Failed to update days off.');
                }
            }
        },
        [loaded.user, etag, daysOff],
    );

    const value = useMemo<CurrentUserContextValue>(
        () => ({
            status: loaded.status,
            user: loaded.user,
            daysOff,
            daysOffVersion,
            canEditDaysOff: etag !== null,
            toggleDayOff,
            error: localError ?? loaded.error,
        }),
        [loaded.status, loaded.user, loaded.error, daysOff, daysOffVersion, etag, toggleDayOff, localError],
    );

    return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
};
